import type { ReactElement } from "react";
import { ArticleHero } from "../ArticleHero";
import maternityHeroImage from "../../assets/images/maternity-outdoor-fall-red-gown investment.webp";
import maternityModernImage from "../../assets/images/maternity-modern-blue-light-studio.webp";
import maternityBlushGownImage from "../../assets/images/maternity-studio-blush-gown.webp";
import { ArticlePhoto } from "./ArticlePhoto";
import { UiIcon } from "./components/UiIcons";
import { GuideCtaActions } from "./components/GuideCtaActions";
import "./NewbornPages.scss";
import "./NewbornArticle.scss";

const prepSteps = [
  {
    when: "ONE WEEK BEFORE",
    title: "Confirm the plan",
    text: "Review the location, start time, outfits and weather backup. Try on every complete look.",
  },
  {
    when: "THE DAY BEFORE",
    title: "Keep things gentle",
    text: "Pack your bag, steam clothing, hydrate normally and avoid experimenting with new skincare.",
  },
  {
    when: "PHOTO DAY",
    title: "Leave room to breathe",
    text: "Eat, wear loose clothing while getting ready and arrive without rushing.",
  },
];

export const HowToPrepareForMaternitySession = (): ReactElement => (
  <main className="newborn-page newborn-article-page maternity-article-page">
    <article>
      <ArticleHero
        breadcrumbs={[
          { label: "Home", to: "/" },
          { label: "Resources", to: "/resources/" },
          { label: "Maternity", to: "/resources/maternity/" },
          { label: "How to Prepare for Your Maternity Photo Session" },
        ]}
        category="Maternity preparation guide"
        description="A calm, practical checklist for the week before your session, photo day and everything in between."
        imageAlt="Expectant mother prepared for a relaxed maternity photo session"
        imagePosition="center 43%"
        imageSrc={maternityHeroImage}
        readingTime="6 min read"
        title="How to Prepare for Your Maternity Photo Session"
        updatedAt="2026-08-04"
        updatedLabel="Updated August 2026"
      />

      <div className="newborn-shell newborn-article-layout">
        <aside className="newborn-article-callout">
          <span className="newborn-article-callout__icon">
            <UiIcon name="star" />
          </span>
          <div>
            <h2>Quick answer</h2>
            <p>
              Confirm your outfits and location a week ahead, pack a small essentials bag the day before, and keep photo
              day spacious and unhurried. You do not need to practice posing—I will guide you throughout the session.
            </p>
          </div>
        </aside>

        <nav aria-label="In this guide" className="newborn-article-toc">
          <h2>In this guide</h2>
          <ol>
            <li>
              <a href="#plan">Your preparation plan</a>
            </li>
            <li>
              <a href="#outfits">Finalize outfits</a>
            </li>
            <li>
              <a href="#self-care">Simple self-care</a>
            </li>
            <li>
              <a href="#pack">What to pack</a>
            </li>
            <li>
              <a href="#family">Prepare your family</a>
            </li>
            <li>
              <a href="#session">During the session</a>
            </li>
          </ol>
        </nav>

        <section className="newborn-article-section" id="plan">
          <p className="newborn-article-kicker">A CALM COUNTDOWN</p>
          <h2>Your maternity session preparation plan</h2>
          <div className="maternity-prep-plan">
            {prepSteps.map((step, index) => (
              <article key={step.when}>
                <span>{index + 1}</span>
                <div>
                  <small>{step.when}</small>
                  <h3>{step.title}</h3>
                  <p>{step.text}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="newborn-article-section newborn-article-split" id="outfits">
          <div>
            <p className="newborn-article-kicker">ABOUT ONE WEEK BEFORE</p>
            <h2>Finalize complete outfits</h2>
            <p>
              Try on clothing with the exact undergarments, shoes and accessories you plan to use. Sit down, walk and
              raise your arms to make sure everything stays comfortable and photographs well from every angle.
            </p>
            <p>
              Steam pieces early and hang them together. If your family is joining, place all outfits side by side to
              check that the colors coordinate without matching exactly.
            </p>
          </div>
          <ArticlePhoto
            alt="Maternity session outfit prepared in advance"
            className="newborn-article-split__photo"
            label="PHOTO: OUTFIT PLAN"
            orientation="portrait"
            src={maternityBlushGownImage}
          />
        </section>

        <section className="newborn-article-section" id="self-care">
          <p className="newborn-article-kicker">FEEL LIKE YOURSELF</p>
          <h2>Keep self-care familiar and simple</h2>
          <p>
            Hydrate as you normally would, rest when you can and avoid new facials, tanning products or beauty
            treatments immediately before the session. Schedule nails or hair with enough time to fix anything you do
            not love.
          </p>
          <aside className="newborn-article-callout newborn-article-callout--tip">
            <span className="newborn-article-callout__icon">
              <UiIcon name="tip" />
            </span>
            <div>
              <h3>No need to arrive knowing how to pose</h3>
              <p>I guide your hands, posture and movement while adapting everything to your comfort and mobility.</p>
            </div>
          </aside>
        </section>

        <section className="newborn-article-section" id="pack">
          <p className="newborn-article-kicker">YOUR SMALL ESSENTIALS BAG</p>
          <h2>What to pack</h2>
          <ul className="newborn-article-checklist">
            <li>
              <UiIcon name="check" />
              <span>Water and an easy snack.</span>
            </li>
            <li>
              <UiIcon name="check" />
              <span>Comfortable walking shoes plus your photo shoes.</span>
            </li>
            <li>
              <UiIcon name="check" />
              <span>Lip color, powder, hairbrush and a few pins.</span>
            </li>
            <li>
              <UiIcon name="check" />
              <span>A light layer, seamless undergarments and any meaningful ultrasound or keepsake.</span>
            </li>
          </ul>
        </section>

        <section className="newborn-article-section newborn-article-split newborn-article-split--reverse" id="family">
          <ArticlePhoto
            alt="Pregnant woman wearing a modern black outfit during a colorful studio maternity session"
            className="newborn-article-split__photo"
            label="PHOTO: FAMILY"
            src={maternityModernImage}
          />
          <div>
            <p className="newborn-article-kicker">PARTNER AND CHILDREN</p>
            <h2>Give family members a simple plan</h2>
            <p>
              Tell children that the session includes movement and play, not a long stretch of standing still. Bring a
              clean, non-messy snack and one comfort item for little ones.
            </p>
            <p>
              Your partner only needs to arrive dressed and ready to connect. I will guide where to stand, where to look
              and what to do with hands.
            </p>
          </div>
        </section>

        <section className="newborn-article-section" id="session">
          <p className="newborn-article-kicker">ON THE DAY</p>
          <h2>What happens during the session</h2>
          <p>
            We begin slowly, choose the easiest light and build variety through small changes in position and
            connection. There is always time to sit, drink water or adjust an outfit. Tell me immediately if anything
            feels uncomfortable—the plan is flexible, and your wellbeing comes first.
          </p>
        </section>

        <section className="newborn-article-cta maternity-article-cta">
          <div>
            <p className="newborn-article-kicker">A RELAXED EXPERIENCE</p>
            <h2>Ready to make a simple session plan?</h2>
            <p>
              Review the maternity session and pricing, or contact me with your due date if you need planning guidance.
            </p>
          </div>
          <GuideCtaActions category="maternity" />
        </section>
      </div>
    </article>
  </main>
);

export default HowToPrepareForMaternitySession;
